import denunciationsApi from 'api/requests/denunciations'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import ModalV2 from 'components/_UI/ModalV2'
import Text from 'components/_UI/Text'
import TextArea from 'components/_UI/TextArea'
import { CheckCircle2, Flag, ImagePlus, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useForm } from 'react-hook-form'
import Select from 'react-select'
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import fieldMessages from 'texts/messages/field'

const MAX_IMAGES = 5;
const MAX_IMAGE_SIZE_MB = 5;
const DESCRIPTION_MIN_LENGTH = 10;
const DESCRIPTION_MAX_LENGTH = 1000;

const acceptedImages = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp']
};

const descriptionValidations = {
    required: fieldMessages.required('descrição'),
    minLength: {
        value: DESCRIPTION_MIN_LENGTH,
        message: fieldMessages.minValue(DESCRIPTION_MIN_LENGTH)
    },
    maxLength: {
        value: DESCRIPTION_MAX_LENGTH,
        message: fieldMessages.maxValue(DESCRIPTION_MAX_LENGTH)
    }
};

export default function ReportEvent({ eventId }) {
    const [visible, setVisible] = useState(false);
    const [reasons, setReasons] = useState([]);
    const [loadingReasons, setLoadingReasons] = useState(false);
    const [reasonsError, setReasonsError] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);
    const [reasonError, setReasonError] = useState(false);
    const [images, setImages] = useState([]);
    const [imagesError, setImagesError] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({ defaultValues: { description: '' } });

    const description = watch('description') || '';

    // Os motivos só são buscados quando alguém abre o modal: denunciar é raro e
    // a página do evento não precisa carregar essa lista para todo visitante.
    useEffect(() => {
        if (!visible || reasons.length) {
            return;
        }

        getReasons();
    }, [visible]);

    const { getRootProps, getInputProps } = useDropzone({
        accept: acceptedImages,
        maxSize: MAX_IMAGE_SIZE_MB * 1024 * 1024,
        multiple: true,
        onDrop: (accepted) => acceptImages(accepted),
        onDropRejected: (rejections) => {
            const code = rejections?.[0]?.errors?.[0]?.code;

            if (code === 'file-too-large') {
                setImagesError(`Cada imagem deve ter no máximo ${MAX_IMAGE_SIZE_MB} MB.`);
                return;
            }

            setImagesError('Formato não aceito. Envie imagens JPG, PNG ou WEBP.');
        }
    });

    async function getReasons() {
        setLoadingReasons(true);
        setReasonsError(false);

        try {
            const { data } = await denunciationsApi.getReasons();
            setReasons(data || []);
        } catch {
            setReasonsError(true);
        } finally {
            setLoadingReasons(false);
        }
    }

    function acceptImages(accepted) {
        const availableSlots = MAX_IMAGES - images.length;

        if (availableSlots <= 0) {
            setImagesError(`Você pode anexar no máximo ${MAX_IMAGES} imagens.`);
            return;
        }

        setImagesError(accepted.length > availableSlots ? `Cabem apenas mais ${availableSlots} imagem(ns).` : '');
        setImages(previous => [
            ...previous,
            ...accepted.slice(0, availableSlots).map(file => ({
                file,
                preview: URL.createObjectURL(file)
            }))
        ]);
    }

    function removeImage(removedIndex) {
        URL.revokeObjectURL(images[removedIndex].preview);
        setImages(previous => previous.filter((_, index) => index !== removedIndex));
        setImagesError('');
    }

    function createPayload(content) {
        const payload = new FormData();

        payload.append('event_id', eventId);
        payload.append('reason', selectedReason.value);
        payload.append('description', content.description);
        images.forEach(image => payload.append('images[]', image.file));

        return payload;
    }

    function errorMessage(error) {
        const status = error?.response?.status;

        if (status === 403) {
            return error?.response?.data?.message || 'Você já denunciou esse evento antes.';
        }

        if (status === 429) {
            return 'Muitas denúncias enviadas. Aguarde alguns minutos e tente novamente.';
        }

        if (status === 422) {
            return 'Confira os dados da denúncia e tente novamente.';
        }

        return 'Não foi possível enviar sua denúncia. Tente novamente.';
    }

    async function onSubmit(content) {
        if (!selectedReason) {
            setReasonError(true);
            return;
        }

        setLoading(true);
        setSubmitError('');

        try {
            await denunciationsApi.create(createPayload(content));
            setSubmitted(true);
        } catch (error) {
            setSubmitError(errorMessage(error));
        } finally {
            setLoading(false);
        }
    }

    function closeModal() {
        images.forEach(image => URL.revokeObjectURL(image.preview));

        setVisible(false);
        setImages([]);
        setImagesError('');
        setSelectedReason(null);
        setReasonError(false);
        setSubmitError('');
        setSubmitted(false);
        reset();
    }

    return (
        <>
            <ReportCard>
                <ReportInfo>
                    <Flag size={18} color={colors.night} />

                    <Column>
                        <Text type='bold' color={colors.nightDark} textAlign='left' paddingBottom={0}>
                            Algo errado com este evento?
                        </Text>

                        <Text name='small' color={colors.night} textAlign='left' paddingBottom={0}>
                            Nossa equipe analisa cada denúncia recebida.
                        </Text>
                    </Column>
                </ReportInfo>

                <ReportAction type='button' onClick={() => setVisible(true)}>
                    Denunciar evento
                </ReportAction>
            </ReportCard>

            <ModalV2
                isOpen={visible}
                size='medium'
                align='top-center'
                mobileSheet
                onClose={closeModal}
            >
                <ModalBody>
                {submitted ? (
                    <SuccessState>
                        <SuccessBadge>
                            <CheckCircle2 size={34} color={colors.occasionalGreen} />
                        </SuccessBadge>

                        <Text name='subtitle' type='bold' marginTop='16px' textAlign='center'>
                            Denúncia enviada
                        </Text>

                        <SuccessMessage>
                            <Text color={colors.night} textAlign='center'>
                                Nossa equipe vai analisar o evento. Obrigado por ajudar a manter a plataforma segura.
                            </Text>
                        </SuccessMessage>

                        <Column width='220px' marginTop='22px'>
                            <Button variant='featured' onClick={closeModal}>Fechar</Button>
                        </Column>
                    </SuccessState>
                ) : (
                    <>
                        <Text name='highlight' textAlign='left'>Denunciar evento</Text>

                        <Text name='small' color={colors.night} textAlign='left' marginBottom='14px'>
                            Conte o que está errado neste evento. Sua denúncia é analisada pela nossa equipe.
                        </Text>

                        {loadingReasons ? (
                            <Column marginTop='20px' marginBottom='20px'>
                                <Loader />
                            </Column>
                        ) : (
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <Column>
                                    <FieldLabel>Motivo da denúncia *</FieldLabel>

                                    <CustomSelect>
                                        <Select
                                            placeholder='Selecione o motivo'
                                            value={selectedReason}
                                            options={reasons}
                                            isDisabled={reasonsError}
                                            noOptionsMessage={() => 'Nenhum motivo disponível'}
                                            onChange={(option) => {
                                                setSelectedReason(option);
                                                setReasonError(false);
                                            }}
                                        />
                                    </CustomSelect>

                                    {reasonError && (
                                        <FieldError>{fieldMessages.required('motivo')}</FieldError>
                                    )}

                                    {reasonsError && (
                                        <FieldError>
                                            Não foi possível carregar os motivos.
                                            <RetryButton type='button' onClick={getReasons}>Tentar novamente</RetryButton>
                                        </FieldError>
                                    )}
                                </Column>

                                <Column marginTop='14px'>
                                    <FieldLabel>Descreva a denúncia *</FieldLabel>

                                    <TextArea
                                        squired
                                        rows={5}
                                        name='description'
                                        register={register}
                                        validations={descriptionValidations}
                                        maxLength={DESCRIPTION_MAX_LENGTH}
                                        placeholder='Explique o que aconteceu com o máximo de detalhes que puder.'
                                    />

                                    <CounterRow>
                                        {errors?.description?.message ? (
                                            <FieldError>{errors.description.message}</FieldError>
                                        ) : <span />}

                                        <Counter>{description.length}/{DESCRIPTION_MAX_LENGTH}</Counter>
                                    </CounterRow>
                                </Column>

                                <Column marginTop='6px'>
                                    <FieldLabel>Imagens (opcional)</FieldLabel>

                                    {images.length < MAX_IMAGES && (
                                        <Dropzone {...getRootProps()}>
                                            <input {...getInputProps()} />
                                            <ImagePlus size={20} color={colors.occasionalPurple} />
                                            <Text name='small' color={colors.night} textAlign='center'>
                                                Toque para anexar até {MAX_IMAGES} imagens (JPG, PNG ou WEBP, até {MAX_IMAGE_SIZE_MB} MB cada)
                                            </Text>
                                        </Dropzone>
                                    )}

                                    {!!images.length && (
                                        <PreviewList>
                                            {images.map((image, index) => (
                                                <Preview key={image.preview}>
                                                    <PreviewImage src={image.preview} alt={`Anexo ${index + 1}`} />
                                                    <RemoveImage
                                                        type='button'
                                                        aria-label={`Remover anexo ${index + 1}`}
                                                        onClick={() => removeImage(index)}
                                                    >
                                                        <X size={13} color={colors.neutralLight} />
                                                    </RemoveImage>
                                                </Preview>
                                            ))}
                                        </PreviewList>
                                    )}

                                    {imagesError && <FieldError>{imagesError}</FieldError>}
                                </Column>

                                {submitError && (
                                    <SubmitError>{submitError}</SubmitError>
                                )}

                                {loading ? (
                                    <Column marginTop='15px'>
                                        <Loader />
                                    </Column>
                                ) : (
                                    <ContentButton>
                                        <Button
                                            variant='light'
                                            category='roundedBorded'
                                            onClick={closeModal}
                                        >
                                            Cancelar
                                        </Button>

                                        <Button variant='featured' type='submit'>
                                            Enviar denúncia
                                        </Button>
                                    </ContentButton>
                                )}
                            </Form>
                        )}
                    </>
                )}
                </ModalBody>
            </ModalV2>
        </>
    )
}

/*
   Mesma receita de card das seções da página (Descrição, Sobre o produtor) para
   o bloco parar de flutuar solto na coluna. O padding é menor e o título não é
   roxo de propósito: denunciar é utilidade de rodapé, não mais uma seção de
   conteúdo disputando atenção com a descrição do evento.
*/
const ReportCard = styled(Column)`
    background: ${colors.neutralLight};
    box-shadow: 0 0 4px #ccc;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    gap: 14px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const ReportInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ReportAction = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 14px;
    border-radius: 20px;
    border: 1.5px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    color: ${colors.night};
    font-family: inherit;
    font-size: ${fonts.sizes.small};
    font-weight: ${fonts.weight.bold};
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${colors.danger};
        background: ${colors.danger}12;
        color: ${colors.danger};
    }
`;

/*
   O StyledModalV2 está com o próprio padding comentado, então quem monta o
   conteúdo é que precisa afastá-lo das bordas — sem isso os campos encostam
   na borda do modal e as laterais das bordas somem.
*/
const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: 22px 20px 20px 20px;
`;

/*
   O modal 'medium' tem min-height de 400px, e a confirmação ocupa bem menos que
   isso. Sem centralizar, sobrava um vazio grande embaixo do botão; com flex: 1
   a sobra vira respiro em cima e embaixo, sem depender de altura fixa.
*/
const SuccessState = styled(Column)`
    flex: 1;
    align-items: center;
    justify-content: center;
    padding-bottom: 10px;
`;

const SuccessBadge = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${colors.occasionalGreen}1A;
`;

const SuccessMessage = styled.div`
    max-width: 340px;
    margin-top: 6px;
`;

const Form = styled.form`
    width: 100%;
`;

const FieldLabel = styled.label`
    color: ${colors.nightDark};
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    text-align: left;
    margin-bottom: 4px;
`;

const FieldError = styled.span`
    color: ${colors.danger};
    font-size: ${fonts.sizes.small};
    text-align: left;
`;

const RetryButton = styled.button`
    margin-left: 6px;
    padding: 0;
    border: none;
    background: transparent;
    color: ${colors.occasionalPurple};
    font-size: ${fonts.sizes.small};
    font-weight: ${fonts.weight.bold};
    cursor: pointer;
`;

const CustomSelect = styled.div`
    width: 100%;

    & > div > div {
        text-align: left;
        font-size: ${fonts.sizes.small};
    }
`;

const CounterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 4px;
`;

const Counter = styled.span`
    color: ${colors.nightLight};
    font-size: ${fonts.sizes.small};
    white-space: nowrap;
`;

const Dropzone = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 14px;
    border: 1.5px dashed ${colors.night}44;
    border-radius: 10px;
    cursor: pointer;

    &:hover {
        border-color: ${colors.occasionalPurple};
    }
`;

const PreviewList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
`;

const Preview = styled.div`
    position: relative;
    width: 68px;
    height: 68px;
`;

const PreviewImage = styled.img`
    width: 100%;
    height: 100%;
    border-radius: 8px;
    object-fit: cover;
`;

const RemoveImage = styled.button`
    position: absolute;
    top: -6px;
    right: -6px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: ${colors.danger};
    cursor: pointer;
`;

const SubmitError = styled.div`
    margin-top: 12px;
    padding: 8px 10px;
    border-radius: 8px;
    background: ${colors.danger}1A;
    color: ${colors.dangerDark};
    font-size: ${fonts.sizes.small};
    text-align: left;
`;

const ContentButton = styled(Column)`
    margin-top: 15px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 5px;
    }
`;
